import { gql } from '@apollo/client'
import { useActionMutation, type UseMutationProps } from '@ztechapps/common.hook'
import type { Project, StateTransitionArgs } from '@ztechapps/common.contract'
import { ProjectStatusEnum } from '@ztechapps/common.contract'
import { projectDocumentFragment } from './graphql'

/**
 * Mutation to change project status using the generic state transition.
 * Allows transitioning to any valid state.
 */
export const changeProjectStatusQuery = gql`
  mutation changeProjectStatus($input: StateTransitionArgs!) {
    changeProjectStatus(input: $input) {
      ...projectDocumentFragment
    }
  }
  ${projectDocumentFragment}
`

/**
 * Hook for managing project state transitions.
 * Provides convenient wrapper functions that all use the generic changeProjectStatus mutation.
 *
 * @example
 * ```tsx
 * const [{ moveToInProgress, moveToComplete }, { loading, error }] = useProjectStateTransition()
 *
 * // Move to In Progress by _id
 * const result = await moveToInProgress(projectId)
 *
 * // Move to Complete by _id
 * const result = await moveToComplete(projectId)
 * ```
 */
export const useProjectStateTransition = ({ query, ...props } = {} as UseMutationProps) => {
  query = query ?? changeProjectStatusQuery

  const [_changeStatus, result] = useActionMutation<Project, StateTransitionArgs<Project>>({
    query,
    ...props,
  })

  const changeStatus = (targetState: ProjectStatusEnum, projectId: string) => {
    const filter = { _id: projectId }
    return _changeStatus({ targetState, filter }, filter, projectId)
  }

  /**
   * Moves project to Draft status.
   */
  const moveToDraft = (_id: string) => {
    return changeStatus(ProjectStatusEnum.Draft, _id)
  }

  /**
   * Moves project to In Progress status.
   */
  const moveToInProgress = (_id: string) => {
    return changeStatus(ProjectStatusEnum.InProgress, _id)
  }

  /**
   * Moves project to Complete status.
   * This finalizes the project.
   */
  const moveToComplete = (_id: string) => {
    return changeStatus(ProjectStatusEnum.Complete, _id)
  }

  /**
   * Moves project to Cancelled status.
   */
  const moveToCancel = (_id: string) => {
    return changeStatus(ProjectStatusEnum.Cancelled, _id)
  }

  return [
    {
      // Generic mutation function
      changeStatus,

      // Convenience wrapper functions
      moveToDraft,
      moveToInProgress,
      moveToComplete,
      moveToCancel,
    },
    result,
  ] as const
}
